import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Alert, Col, Row } from 'react-bootstrap';
import { getUserById, updateUser } from '../../../../services/UsersServices';
import { getRoles } from '../../../../services/RoleService';
import moment from 'moment';
import DatePicker from 'react-datepicker';
const EditUser = () => {
  const ssoOptions = [
    { value: 'okta', label: 'Okta' },
    { value: 'azure', label: 'Azure' },
  ];
  const navigate = useNavigate();
  const { id } = useParams();
  const [error, setError] = useState('');
  const [initialValues, setInitialValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    employeeNumber: '',
    sub: '',
    locale: '',
    zoneinfo: '',
    role: null,
    manager: '',
    displayName: '',
    managerEmail: '',
    dateOfJoining: null,
    ssoLoginWith: 'okta',
  });
  const [roleOptions, setRoleOptions] = useState([]);
  const [isUpdatePasswordChecked, setIsUpdatePasswordChecked] = useState(false);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await getRoles();
        const options = response.map((data) => ({
          value: data.slug,
          label: data.label,
        }));
        setRoleOptions(options);
      } catch (err) {
        setError('Failed to fetch roles');
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await getUserById(id);
        setInitialValues({
          ...userData,
          role: {
            value: userData.role,
            label: userData.role.charAt(0).toUpperCase() + userData.role.slice(1).toLowerCase(),
          },
          dateOfJoining:
            userData.dateOfJoining && userData.dateOfJoining.toLowerCase() != 'not set'
              ? moment(userData.dateOfJoining).format('MM/DD/YYYY')
              : moment().toDate(),
        });
      } catch (err) {
        setError('Failed to fetch user data');
      }
    };
    fetchUser();
  }, [id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: Yup.object({
      firstName: Yup.string().required("First name can't be blank"),
      lastName: Yup.string().required("Last name can't be blank"),
      email: Yup.string().email('Invalid email').required("Email can't be blank"),
      ...(isUpdatePasswordChecked && {
        password: Yup.string()
          .min(8, 'Password must be at least 8 characters')
          .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
          .matches(/[0-9]/, 'Must contain at least one number')
          .required("Password can't be blank"),
      }),
    }),
    onSubmit: async (values) => {
      let payload = { ...values, role: values.role.value };
      if (!isUpdatePasswordChecked) {
        delete payload.password;
      }
      try {
        await updateUser(id, payload);
        navigate('/settings/users');
      } catch (err) {
        setError('Error updating user');
      }
    },
  });

  const handleCheckboxChange = () => {
    setIsUpdatePasswordChecked(!isUpdatePasswordChecked);
    if (!isUpdatePasswordChecked) {
      formik.setFieldValue('password', '');
    }
  };

  if (!initialValues) {
    return <div>Loading...</div>;
  }

  return (
    <Form onSubmit={formik.handleSubmit}>
      <h2>Edit User</h2>
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Name Fields */}
      <Row>
        <Col>
          <Form.Group controlId="firstName">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              {...formik.getFieldProps('firstName')}
              isInvalid={formik.touched.firstName && !!formik.errors.firstName}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.firstName}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="lastName">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              {...formik.getFieldProps('lastName')}
              isInvalid={formik.touched.lastName && !!formik.errors.lastName}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.lastName}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group controlId="employeeNumber">
            <Form.Label>Employee Number</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('employeeNumber')} />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="sub">
            <Form.Label>Sub</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('sub')} />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group controlId="locale">
            <Form.Label>Locale</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('locale')} />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="zoneinfo">
            <Form.Label>Zone Info</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('zoneinfo')} />
          </Form.Group>
        </Col>
      </Row>
      {/* Role Select */}
      <Row>
        <Col>
          <Form.Group controlId="role">
            <Form.Label>Role</Form.Label>
            <Select
              options={roleOptions}
              value={formik.values.role}
              onChange={(option) => formik.setFieldValue('role', option)}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="manager">
            <Form.Label>Manager</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('manager')} />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group controlId="displayName">
            <Form.Label>Display Name</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('displayName')} />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="managerEmail" className="mb-3">
            <Form.Label>Manager Email</Form.Label>
            <Form.Control type="text" {...formik.getFieldProps('managerEmail')} />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <Form.Group controlId="email">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              {...formik.getFieldProps('email')}
              isInvalid={formik.touched.email && !!formik.errors.email}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.email}</Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col>
          <Form.Group controlId="dateOfJoining">
            <Form.Label>Hire Date</Form.Label>
            <DatePicker
              selected={
                formik.values.dateOfJoining
                  ? moment(formik.values.dateOfJoining, 'MM/DD/YYYY').toDate()
                  : null
              }
              onChange={(date) =>
                formik.setFieldValue(
                  'dateOfJoining',
                  date ? moment(date).format('MM/DD/YYYY') : null
                )
              }
              className="form-control"
              dateFormat="MM/dd/yyyy"
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.dateOfJoining}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="ssoLoginWith">
            <Form.Label>SSO Login with</Form.Label>
            <Select
              name="ssoLoginWith"
              options={ssoOptions}
              value={ssoOptions.find((option) => option.value === formik.values.ssoLoginWith)}
              onChange={(selectedOption) => {
                formik.setFieldValue('ssoLoginWith', selectedOption ? selectedOption.value : '');
              }}
              onBlur={() => formik.setFieldTouched('ssoLoginWith', true)}
              className={
                formik.touched.ssoLoginWith && formik.errors.ssoLoginWith ? 'is-invalid' : ''
              }
            />
            {formik.touched.ssoLoginWith && formik.errors.ssoLoginWith && (
              <div className="invalid-feedback d-block">{formik.errors.ssoLoginWith}</div>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row className="mt-2">
        <Col className="mt-4 mb-1">
          <Form.Group controlId="updatePassword">
            <Form.Check
              type="checkbox"
              label="Update Password"
              checked={isUpdatePasswordChecked}
              onChange={handleCheckboxChange}
            />
          </Form.Group>
        </Col>
        <Col>
          {isUpdatePasswordChecked && (
            <Form.Group controlId="password">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                {...formik.getFieldProps('password')}
                isInvalid={formik.touched.password && !!formik.errors.password}
              />
              <Form.Control.Feedback type="invalid">{formik.errors.password}</Form.Control.Feedback>
            </Form.Group>
          )}
        </Col>
      </Row>

      <Button variant="primary" type="submit">
        Update User
      </Button>
    </Form>
  );
};

export default EditUser;
